import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { Link } from "react-router-dom";
import "../styles/AdminDashboardPage.scss"; // reuse same styling for tables/buttons

type ModelRow = {
  id: string;
  name: string;
  category?: string;
  instagram?: string;
  instagramHandle?: string; // legacy field
  heroUrl?: string;
  images?: Array<string | { url: string; caption?: string }>;
  createdAt?: { seconds: number } | any;
  active?: boolean;
};

const AdminModelsList: React.FC = () => {
  const [models, setModels] = useState<ModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "models"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as ModelRow[];
      setModels(rows);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return models;
    return models.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(term) ||
        (m.category || "").toLowerCase().includes(term) ||
        (m.instagram || m.instagramHandle || "").toLowerCase().includes(term)
    );
  }, [q, models]);

  const remove = async (id: string) => {
    if (!window.confirm("Delete this model? This only removes the Firestore document (images stay in Storage).")) {
      return;
    }
    await deleteDoc(doc(db, "models", id));
    setModels((prev) => prev.filter((m) => m.id !== id));
  };

  const getThumb = (row: ModelRow): string | undefined => {
    if (row.heroUrl) return row.heroUrl;
    const imgs = row.images;
    if (!imgs || imgs.length === 0) return undefined;
    const first = imgs[0] as any;
    return typeof first === "string" ? first : first?.url;
  };

  const safeDate = (row: ModelRow) => {
    const s = row.createdAt?.seconds;
    if (typeof s === "number") return new Date(s * 1000).toLocaleDateString();
    return "—";
  };

  if (loading) return <p>Loading models…</p>;

  return (
    <div className="admin-dashboard">
      <h2>Models</h2>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, category, or Instagram…"
          className="search-input"
        />
        <Link to="/admin/add-model" className="add-btn">
          + Add Model
        </Link>
      </div>

      <table className="events-table">
        <thead>
          <tr>
            <th>Preview</th>
            <th>Name</th>
            <th>Category</th>
            <th>Instagram</th>
            <th>Images</th>
            <th>Active</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((m) => {
            const thumb = getThumb(m);
            const insta = m.instagram || m.instagramHandle || "";
            const imgCount = Array.isArray(m.images) ? m.images.length : 0;

            return (
              <tr key={m.id}>
                <td>{thumb && <img src={thumb} alt="" className="thumbnail-preview" />}</td>
                <td>{m.name}</td>
                <td>{m.category || "—"}</td>
                <td>
                  {insta ? (
                    <a
                      href={`https://instagram.com/${insta.replace("@", "")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {insta}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{imgCount}</td>
                <td>{m.active ? "Yes" : "No"}</td>
                <td>{safeDate(m)}</td>
                <td style={{ display: "flex", gap: 8 }}>
                  <Link to={`/admin/models/${m.id}/edit`} className="edit-btn">
                    Edit
                  </Link>
                  <button onClick={() => remove(m.id)} className="delete-btn">
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}

          {filtered.length === 0 && (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", opacity: 0.8 }}>
                No models found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminModelsList;
